/* =========================================================================
   compare.ts — pure cross-scenario comparison derivation.
   Zero React imports. Consumes frozen types from platform-types.ts.
   ========================================================================= */

import type {
  BuildCompareViewModel,
  CompareCategory,
  CompareCell,
  CompareRow,
  CompareViewModel,
} from "./platform-types";
import type { ModelResult, Scenario } from "./types";
import { Fmt } from "./format";
import { formatReportingCurrency, type FxSnapshot } from "./reporting-currency";
import { computeDatedFinance } from "./dated-finance";
import { computeDatedOwnership } from "./dated-ownership";
import { resolveScenarioOwnershipInput } from "./scenario-ownership";
import type { DatedFinanceResult } from "./dated-finance-types";
import {
  PEAK_CONSTRUCTION_DEBT_LABEL,
  EQUITY_REQUIRED_LABEL,
  OPERATOR_IRR_LABEL,
  OPERATOR_NPV_LABEL,
  Y1_EBITDA_LABEL,
  AVG_EBITDA_LABEL,
  RENT_PCT_REVENUE_Y1_LABEL,
  BREAK_EVEN_RENT_Y1_LABEL,
  matchedHorizonLabel,
} from "./presentation/return-labels";
import {
  affordabilityRatio,
  catchmentFor,
  estimatedEnrolmentPool,
  type CensusArea,
} from "./census";

export type {
  CompareCell,
  CompareRow,
  CompareViewModel,
} from "./platform-types";

export type CompareRowDescriptor = {
  key: string;
  label: string;
  category: CompareCategory;
  unit: CompareRow["unit"];
  extract: (scenario: Scenario, result: ModelResult) => number | null;
  caveat?: (result: ModelResult) => boolean;
  /** Rows whose value is computed on the developer's selected exit strategy declare it, so two scenarios
   *  with different strategies are never compared as if like-for-like. */
  basis?: (scenario: Scenario, result: ModelResult) => string;
};

/** The exit strategy the exit-dependent developer metrics are computed on — worded by the label hub
 *  ("sale at completion" / "Y5 hold-and-sell") so the compare tag matches every other surface. */
export function developerExitBasis(scenario: Scenario): string {
  return matchedHorizonLabel(
    scenario.assumptions.developerExitYear ?? scenario.assumptions.modelYears,
    scenario.assumptions.modelYears,
  );
}

function resolveCatchment(scenario: Scenario): CensusArea {
  return catchmentFor(scenario);
}

function weightedOnlineYear(scenario: Scenario): number | null {
  const buildings = scenario.buildings || [];
  if (buildings.length === 0) return null;
  const totalGfa = buildings.reduce((sum, b) => sum + (b.gfaSqm || 0), 0);
  if (totalGfa <= 0) return null;
  const weighted = buildings.reduce(
    (sum, b) => sum + (b.gfaSqm || 0) * (b.comesOnlineYear || 0),
    0,
  );
  return weighted / totalGfa;
}

export const COMPARE_ROWS: CompareRowDescriptor[] = [
  // ---- land ----
  {
    key: "land.purchasePrice",
    label: "Purchase price",
    category: "land",
    unit: "currency",
    extract: (_s, r) => r.developer.purchasePrice,
  },
  {
    key: "land.stampDuty",
    label: "Stamp duty",
    category: "land",
    unit: "currency",
    extract: (_s, r) => r.developer.stampDuty,
  },
  {
    key: "land.acquisitionFee",
    label: "Acquisition fee",
    category: "land",
    unit: "currency",
    extract: (_s, r) => r.developer.acquisitionFee,
  },
  {
    key: "land.totalLandIn",
    label: "Total land-in cost",
    category: "land",
    unit: "currency",
    extract: (_s, r) =>
      r.developer.purchasePrice +
      r.developer.stampDuty +
      r.developer.acquisitionFee,
  },

  // ---- cost stack ----
  {
    key: "costStack.hardConstruction",
    label: "Hard construction",
    category: "costStack",
    unit: "currency",
    extract: (_s, r) => r.developer.hardConstruction,
  },
  {
    key: "costStack.preliminaries",
    label: "Preliminaries",
    category: "costStack",
    unit: "currency",
    extract: (_s, r) => r.developer.preliminaries,
  },
  {
    key: "costStack.builderMargin",
    label: "Builder margin",
    category: "costStack",
    unit: "currency",
    extract: (_s, r) => r.developer.builderMargin,
  },
  {
    key: "costStack.consultantCosts",
    label: "Consultant costs",
    category: "costStack",
    unit: "currency",
    extract: (_s, r) => r.developer.consultantCosts,
  },
  {
    key: "costStack.statutoryCosts",
    label: "Statutory costs",
    category: "costStack",
    unit: "currency",
    extract: (_s, r) => r.developer.statutoryCosts,
  },
  {
    key: "costStack.constructionContingency",
    label: "Construction contingency",
    category: "costStack",
    unit: "currency",
    extract: (_s, r) => r.developer.constructionContingency,
  },
  {
    key: "costStack.holdingInterest",
    label: "Holding interest",
    category: "costStack",
    unit: "currency",
    extract: (_s, r) => r.developer.holdingInterest,
  },
  {
    key: "costStack.developmentManagementFee",
    label: "DM fee",
    category: "costStack",
    unit: "currency",
    extract: (_s, r) => r.developer.developmentManagementFee,
  },
  {
    key: "costStack.dmFeeBase",
    label: "DM fee base",
    category: "costStack",
    unit: "currency",
    extract: (_s, r) => r.developer.dmFeeBase,
  },
  {
    key: "costStack.totalCost",
    label: "Total development cost",
    category: "costStack",
    unit: "currency",
    extract: (_s, r) => r.developer.totalCost,
  },

  // ---- build timing ----
  {
    key: "buildTiming.totalGfaSqm",
    label: "Total GFA",
    category: "buildTiming",
    unit: "count",
    extract: (_s, r) => r.campus.totalGfaSqm,
  },
  {
    key: "buildTiming.totalNlaSqm",
    label: "Total NLA",
    category: "buildTiming",
    unit: "count",
    extract: (_s, r) => r.campus.totalNlaSqm,
  },
  {
    key: "buildTiming.weightedOnlineYear",
    label: "Weighted online year",
    category: "buildTiming",
    unit: "year",
    extract: (s, _r) => weightedOnlineYear(s),
  },
  {
    key: "buildTiming.lastCompletionMonth",
    label: "Last completion month",
    category: "buildTiming",
    unit: "months",
    extract: (_s, r) => r.developer.lastCompletionMonth,
  },

  // ---- rent ----
  {
    key: "rent.year1NetRent",
    label: "Year-1 net rent",
    category: "rent",
    unit: "currency",
    extract: (_s, r) => r.campus.totalYear1NetRent,
  },
  {
    key: "rent.blendedRentPerSqm",
    label: "Blended rent $/sqm",
    category: "rent",
    unit: "currency",
    extract: (_s, r) =>
      r.campus.totalNlaSqm > 0
        ? r.campus.totalYear1NetRent / r.campus.totalNlaSqm
        : null,
  },
  {
    key: "rent.grossRent",
    label: "Gross rent",
    category: "rent",
    unit: "currency",
    extract: (_s, r) =>
      r.campus.totalYear1NetRent + r.campus.totalYear1Outgoings,
  },

  // ---- developer returns ----
  {
    key: "developerReturns.yieldOnCost",
    label: "Yield on cost",
    category: "developerReturns",
    unit: "pct",
    extract: (_s, r) => r.developer.yieldOnCost,
  },
  {
    key: "developerReturns.marginOnCost",
    basis: developerExitBasis,
    label: "Margin on cost",
    category: "developerReturns",
    unit: "pct",
    extract: (_s, r) => r.developer.marginOnCost,
  },
  {
    key: "developerReturns.ungearedIrr",
    label: "Ungeared IRR",
    category: "developerReturns",
    unit: "pct",
    extract: (_s, r) => r.developer.irr,
  },
  {
    key: "developerReturns.gearedIrr",
    label: "Geared IRR",
    category: "developerReturns",
    unit: "pct",
    extract: (_s, r) => r.developer.gearedDevelopmentIrr,
  },
  {
    key: "developerReturns.npv",
    label: "Developer NPV",
    category: "developerReturns",
    unit: "currency",
    extract: (_s, r) => r.kpis.developerNpv,
  },
  {
    key: "developerReturns.peakDebt",
    label: PEAK_CONSTRUCTION_DEBT_LABEL,
    category: "developerReturns",
    unit: "currency",
    extract: (_s, r) => r.developer.peakDebtDev,
  },
  {
    key: "developerReturns.peakEquity",
    label: EQUITY_REQUIRED_LABEL,
    category: "developerReturns",
    unit: "currency",
    extract: (_s, r) => r.developer.equityRequired,
  },
  {
    key: "developerReturns.moneyMultiple",
    basis: developerExitBasis,
    label: "Money multiple",
    category: "developerReturns",
    unit: "ratio",
    extract: (_s, r) => r.developer.moneyMultiple,
  },

  // ---- operator affordability ----
  {
    key: "operatorAffordability.year1Ebitda",
    label: Y1_EBITDA_LABEL,
    category: "operatorAffordability",
    unit: "currency",
    extract: (_s, r) => r.kpis.yearOneEbitda,
  },
  {
    key: "operatorAffordability.avgEbitda",
    label: AVG_EBITDA_LABEL,
    category: "operatorAffordability",
    unit: "currency",
    extract: (_s, r) => r.kpis.avgEbitda,
  },
  {
    key: "operatorAffordability.operatorNpv",
    label: OPERATOR_NPV_LABEL,
    category: "operatorAffordability",
    unit: "currency",
    extract: (_s, r) => r.kpis.reddamNpv,
  },
  {
    key: "operatorAffordability.rentPctRevenueY1",
    label: RENT_PCT_REVENUE_Y1_LABEL,
    category: "operatorAffordability",
    unit: "pct",
    extract: (_s, r) => r.kpis.rentPctRevenueYear1,
  },
  {
    key: "operatorAffordability.breakEvenRentY1",
    label: BREAK_EVEN_RENT_Y1_LABEL,
    category: "operatorAffordability",
    unit: "currency",
    extract: (_s, r) => r.kpis.breakEvenRentY1,
    caveat: (r) => r.kpis.breakEvenRentConverged === false,
  },
  {
    key: "operatorAffordability.operatorIrr",
    label: OPERATOR_IRR_LABEL,
    category: "operatorAffordability",
    unit: "pct",
    extract: (_s, r) =>
      Number.isFinite(r.kpis.reddamIrr ?? NaN) ? r.kpis.reddamIrr : null,
  },

  // ---- market (static until CENSUS lands per-scenario catchment) ----
  {
    key: "market.affordabilityRatio",
    label: "Affordability ratio",
    category: "market",
    unit: "ratio",
    extract: (s, _r) =>
      affordabilityRatio(resolveCatchment(s), s.assumptions.feePerStudentYear1),
  },
  {
    key: "market.enrolmentPool",
    label: "Enrolment pool",
    category: "market",
    unit: "count",
    extract: (s, _r) =>
      estimatedEnrolmentPool(
        resolveCatchment(s),
        s.assumptions.enrolmentCaptureRate ?? 0.15,
      ),
  },
];

function formatDisplay(
  unit: CompareRow["unit"],
  v: number | null,
  fx?: FxSnapshot,
): string {
  if (v == null || !Number.isFinite(v)) return "—";
  switch (unit) {
    case "currency":
      return fx ? formatReportingCurrency(v, fx, true) : Fmt.compactCurrency(v);
    case "pct":
      return Fmt.pct(v, 1);
    case "pp":
      return Fmt.pct(v, 1);
    case "year":
      return String(Math.round(v));
    case "months":
      return `${Math.round(v)} mo`;
    case "count":
      return Fmt.num(v, 0);
    case "ratio":
      return Fmt.multiple(v, 2);
    case "date":
      return new Date(v).toISOString().slice(0, 10);
    default:
      return String(v);
  }
}

function compactDeltaCurrency(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(abs / 1e3).toFixed(1)}K`;
  return `$${abs.toFixed(0)}`;
}

function formatDelta(
  unit: CompareRow["unit"],
  n: number | null,
  fx?: FxSnapshot,
): string | null {
  if (n == null || !Number.isFinite(n)) return null;
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  const abs = Math.abs(n);
  switch (unit) {
    case "currency":
      return (
        sign +
        (fx
          ? formatReportingCurrency(abs, fx, true)
          : compactDeltaCurrency(abs))
      );
    case "pct":
    case "pp":
      return `${sign}${(abs * 100).toFixed(1)}pp`;
    case "year":
      return `${sign}${Math.round(abs)}`;
    case "months":
      return `${sign}${Math.round(abs)} mo`;
    case "count":
      return sign + Fmt.num(abs, 0);
    case "ratio":
      return `${sign}${Fmt.multiple(abs, 2)}`;
    case "date":
      return null;
    default:
      return `${sign}${abs}`;
  }
}

type CompareViewModelWithNames = CompareViewModel & { scenarioNames: string[] };

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const dayValue = (value: string | undefined): number | null => {
  if (!value || !ISO_DAY.test(value)) return null;
  const n = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(n) && new Date(n).toISOString().slice(0, 10) === value
    ? n
    : null;
};
const inWindow = (
  date: string | undefined,
  start?: string,
  end?: string,
): boolean => {
  const value = dayValue(date);
  const first = dayValue(start);
  const last = dayValue(end);
  return (
    value !== null &&
    (first === null || value >= first) &&
    (last === null || value <= last)
  );
};

function datedWindowResult(
  scenario: Scenario | undefined,
  valuationDate?: string,
  endDate?: string,
): {
  finance: DatedFinanceResult | null;
  ownership: ReturnType<typeof computeDatedOwnership> | null;
  flows: DatedFinanceResult["cashflows"];
  ledger: DatedFinanceResult["ledger"];
} {
  if (
    !scenario ||
    scenario.calculationMethod !== "dated-v2" ||
    !scenario.datedFinance
  )
    return { finance: null, ownership: null, flows: [], ledger: [] };
  const finance = computeDatedFinance(scenario.datedFinance);
  const ownershipInput = resolveScenarioOwnershipInput(scenario, finance);
  return {
    finance,
    ownership: ownershipInput ? computeDatedOwnership(ownershipInput) : null,
    flows: finance.cashflows.filter((flow) =>
      inWindow(flow.date, valuationDate, endDate),
    ),
    ledger: finance.ledger.filter((line) =>
      inWindow(line.date, valuationDate, endDate),
    ),
  };
}

function xirr(flows: Array<{ date: string; value: number }>): number | null {
  const dated = flows
    .map((flow) => ({ ...flow, time: dayValue(flow.date) }))
    .filter(
      (flow): flow is { date: string; value: number; time: number } =>
        flow.time !== null,
    )
    .sort((a, b) => a.time - b.time);
  let priorSign = 0;
  let signChanges = 0;
  for (const flow of dated) {
    const sign = Math.sign(flow.value);
    if (sign && priorSign && sign !== priorSign) signChanges += 1;
    if (sign) priorSign = sign;
  }
  if (signChanges !== 1) return null;
  const valueAt = (rate: number) =>
    dated.reduce(
      (total, flow) =>
        total +
        flow.value /
          Math.pow(1 + rate, (flow.time - dated[0].time) / 31_536_000_000),
      0,
    );
  let low = -0.9999;
  let high = 1;
  let lowValue = valueAt(low);
  let highValue = valueAt(high);
  while (lowValue * highValue > 0 && high < 1_000_000) {
    high *= 2;
    highValue = valueAt(high);
  }
  if (
    !Number.isFinite(lowValue) ||
    !Number.isFinite(highValue) ||
    lowValue * highValue > 0
  )
    return null;
  for (let i = 0; i < 160; i += 1) {
    const middle = (low + high) / 2;
    const middleValue = valueAt(middle);
    if (Math.abs(middleValue) < 0.000001) return middle;
    if (lowValue * middleValue <= 0) high = middle;
    else {
      low = middle;
      lowValue = middleValue;
    }
  }
  return (low + high) / 2;
}

function xnpv(
  flows: Array<{ date: string; value: number }>,
  rate: number,
  valuationDate?: string,
): number | null {
  const dated = flows
    .map((flow) => ({ ...flow, time: dayValue(flow.date) }))
    .filter(
      (flow): flow is { date: string; value: number; time: number } =>
        flow.time !== null,
    )
    .sort((a, b) => a.time - b.time);
  const valuation = dayValue(valuationDate);
  if (
    dated.length === 0 ||
    valuation === null ||
    !Number.isFinite(rate) ||
    rate <= -1
  )
    return null;
  return dated.reduce(
    (total, flow) =>
      total +
      flow.value / Math.pow(1 + rate, (flow.time - valuation) / 31_536_000_000),
    0,
  );
}

/**
 * A clipped cash-flow window can produce an apparently plausible IRR or
 * equity multiple while omitting the investment or its exit. We have no
 * opening asset/debt valuation to bridge that gap, so those are only shown
 * when the selected window contains the complete non-zero return series.
 */
function excludesInvestmentCashflows(
  dated: ReturnType<typeof datedWindowResult>,
  field: "project" | "equity",
  valuationDate?: string,
  endDate?: string,
): boolean {
  return (
    dated.finance?.cashflows.some(
      (flow) =>
        Math.abs(flow[field]) > 0.000001 &&
        !inWindow(flow.date, valuationDate, endDate),
    ) ?? false
  );
}

type DatedMetric = {
  key: string;
  label: string;
  category: CompareCategory;
  unit: CompareRow["unit"];
  value: (
    scenario: Scenario,
    dated: ReturnType<typeof datedWindowResult>,
  ) => number | null;
};

function datedMetrics(
  scenarios: Scenario[],
  valuationDate?: string,
  endDate?: string,
  datedByColumn: ReturnType<typeof datedWindowResult>[] = [],
): DatedMetric[] {
  const buckets = new Set<string>();
  const milestones = new Set<string>();
  for (const dated of datedByColumn)
    for (const bucket of Object.keys(dated.finance?.costBuckets ?? {}))
      buckets.add(bucket);
  for (const scenario of scenarios)
    for (const milestone of scenario.datedFinance?.milestones ?? [])
      if (milestone.id.trim()) milestones.add(milestone.id.trim());
  return [
    {
      key: "programme.startDate",
      label: "Programme start",
      category: "programme",
      unit: "date",
      value: (s) => dayValue(s.datedFinance?.startDate),
    },
    {
      key: "programme.endDate",
      label: "Programme end",
      category: "programme",
      unit: "date",
      value: (s) => dayValue(s.datedFinance?.endDate),
    },
    ...[...milestones].sort().map((id): DatedMetric => ({
      key: `programme.milestone.${id}`,
      label: `Milestone · ${id}`,
      category: "programme",
      unit: "date",
      value: (_s, dated) => dayValue(dated.finance?.resolvedMilestones[id]),
    })),
    ...[...buckets].sort().map((bucket): DatedMetric => ({
      key: `datedCosts.${bucket}`,
      label: `Cost · ${bucket} (source horizon)`,
      category: "costStack",
      unit: "currency",
      value: (_s, dated) => dated.finance?.costBuckets[bucket] ?? 0,
    })),
    {
      key: "funding.peakDebt",
      label: "Peak debt (source horizon)",
      category: "funding",
      unit: "currency",
      value: (_s, dated) => dated.finance?.peakDebt ?? null,
    },
    {
      key: "funding.equityRequired",
      label: "Equity required (source horizon)",
      category: "funding",
      unit: "currency",
      value: (_s, dated) => dated.finance?.equityRequired ?? null,
    },
    {
      key: "rent.landlordRent",
      label: "Landlord rent in horizon",
      category: "rent",
      unit: "currency",
      value: (_scenario, dated) =>
        dated.finance?.complete
          ? dated.ledger
              .filter((line) => line.category === "landlord-rent")
              .reduce((sum, line) => sum + line.project, 0)
          : null,
    },
    {
      key: "operator.netCashflow",
      label: "Operator cash flow in horizon",
      category: "operatorAffordability",
      unit: "currency",
      value: (_scenario, dated) =>
        dated.finance?.complete
          ? dated.ledger.reduce((sum, line) => sum + line.operator, 0)
          : null,
    },
    {
      key: "datedReturns.projectNpv",
      label: "Project NPV in common window",
      category: "developerReturns",
      unit: "currency",
      value: (s, dated) =>
        dated.finance?.complete
          ? xnpv(
              dated.flows.map((flow) => ({
                date: flow.date,
                value: flow.project,
              })),
              dated.finance?.discountRate ??
                s.datedFinance?.discountRate ??
                0.1,
              valuationDate,
            )
          : null,
    },
    {
      key: "datedReturns.equityNpv",
      label: "Equity NPV in common window",
      category: "developerReturns",
      unit: "currency",
      value: (s, dated) =>
        dated.finance?.complete
          ? xnpv(
              dated.flows.map((flow) => ({
                date: flow.date,
                value: flow.equity,
              })),
              dated.finance?.discountRate ??
                s.datedFinance?.discountRate ??
                0.1,
              valuationDate,
            )
          : null,
    },
    {
      key: "datedReturns.projectIrr",
      label: "Project IRR",
      category: "developerReturns",
      unit: "pct",
      value: (_s, dated) =>
        dated.finance?.complete &&
        dated.finance.returnsSettled &&
        !excludesInvestmentCashflows(dated, "project", valuationDate, endDate)
          ? xirr(
              dated.flows.map((flow) => ({
                date: flow.date,
                value: flow.project,
              })),
            )
          : null,
    },
    {
      key: "datedReturns.equityIrr",
      label: "Equity IRR",
      category: "developerReturns",
      unit: "pct",
      value: (_s, dated) =>
        dated.finance?.complete &&
        dated.finance.returnsSettled &&
        !excludesInvestmentCashflows(dated, "equity", valuationDate, endDate)
          ? xirr(
              dated.flows.map((flow) => ({
                date: flow.date,
                value: flow.equity,
              })),
            )
          : null,
    },
    {
      key: "datedReturns.equityMultiple",
      label: "Equity cash multiple",
      category: "developerReturns",
      unit: "ratio",
      value: (_s, dated) => {
        if (
          !dated.finance?.complete ||
          !dated.finance.returnsSettled ||
          excludesInvestmentCashflows(dated, "equity", valuationDate, endDate)
        )
          return null;
        const positive = dated.flows.reduce(
          (sum, flow) => sum + Math.max(0, flow.equity),
          0,
        );
        const negative = dated.flows.reduce(
          (sum, flow) => sum + Math.max(0, -flow.equity),
          0,
        );
        return negative > 0 ? positive / negative : null;
      },
    },
    {
      key: "ownership.advantage",
      label: "Ownership advantage (source ownership horizon)",
      category: "ownership",
      unit: "currency",
      value: (_s, dated) => dated.ownership?.advantage ?? null,
    },
    {
      key: "ownership.leasePv",
      label: "Lease PV (source ownership horizon)",
      category: "ownership",
      unit: "currency",
      value: (_s, dated) => dated.ownership?.pvLeaseCost ?? null,
    },
    {
      key: "ownership.ownPv",
      label: "Own net PV (source ownership horizon)",
      category: "ownership",
      unit: "currency",
      value: (_s, dated) => dated.ownership?.pvOwnNetCost ?? null,
    },
    {
      key: "ownership.peakCash",
      label: "Peak own cash (source ownership horizon)",
      category: "ownership",
      unit: "currency",
      value: (_s, dated) => dated.ownership?.peakCumulativeOwnCash ?? null,
    },
  ];
}

export const buildCompareViewModel: BuildCompareViewModel = ({
  scenarios,
  results,
  scenarioIds,
  baselineId,
  fx,
  valuationDate,
  endDate,
}) => {
  if (scenarioIds.length === 0) {
    return {
      scenarioIds: [],
      baselineId,
      rows: [],
      marketIsStatic: true,
      scenarioNames: [],
      metadata: { valuationDate, endDate, fx },
    } as CompareViewModelWithNames;
  }
  const effectiveBaseline = scenarioIds.includes(baselineId)
    ? baselineId
    : scenarioIds[0];
  const baselineIndex = scenarioIds.indexOf(effectiveBaseline);
  const baselineResult = results[effectiveBaseline];
  const baselineScenario = scenarios[baselineIndex];
  const hasBaseline =
    baselineIndex >= 0 &&
    baselineResult != null &&
    baselineScenario != null &&
    baselineScenario.calculationMethod !== "dated-v2";

  const marketIsStatic = scenarios.every(
    (s) => !s.catchment && !s.assumptions.catchmentCode,
  );
  const datedByColumn = scenarioIds.map((_, index) =>
    datedWindowResult(scenarios[index], valuationDate, endDate),
  );

  const rows: CompareRow[] = COMPARE_ROWS.map((descriptor) => {
    // A delta is only a real scenario-over-scenario shift when BOTH operands are solved values. If either
    // side is a caveated fallback (e.g. a non-converged break-even that echoes the scenario's own rent),
    // the subtraction would launder a fabricated operand into a precise-looking swing — suppress it and
    // say why, exactly as every other consumer gates the raw value on the converged flag.
    const baselineCaveated =
      hasBaseline && descriptor.caveat
        ? descriptor.caveat(baselineResult)
        : false;
    const baselineBasis =
      hasBaseline && descriptor.basis
        ? descriptor.basis(baselineScenario, baselineResult)
        : undefined;
    let basisMixed = false;
    const cells: CompareCell[] = scenarioIds.map((id, index) => {
      const scenario = scenarios[index];
      const result = results[id];
      // A dated-v2 scenario must never borrow a legacy result from the older
      // engine. Its migrated assumptions remain provenance only.
      const value =
        result && scenario && scenario.calculationMethod !== "dated-v2"
          ? descriptor.extract(scenario, result)
          : null;
      const caveated =
        result &&
        scenario &&
        scenario.calculationMethod !== "dated-v2" &&
        descriptor.caveat
          ? descriptor.caveat(result)
          : false;

      const basis =
        result &&
        scenario &&
        scenario.calculationMethod !== "dated-v2" &&
        descriptor.basis
          ? descriptor.basis(scenario, result)
          : undefined;
      if (
        basis !== undefined &&
        baselineBasis !== undefined &&
        basis !== baselineBasis
      )
        basisMixed = true;
      const comparable =
        hasBaseline && id !== effectiveBaseline && result && value != null;
      let deltaVsBaseline: number | null = null;
      let deltaSuppressed: CompareCell["deltaSuppressed"];
      if (comparable) {
        if (caveated) deltaSuppressed = "self";
        else if (baselineCaveated) deltaSuppressed = "baseline";
        else if (
          basis !== undefined &&
          baselineBasis !== undefined &&
          basis !== baselineBasis
        )
          deltaSuppressed = "basis";
        else {
          const baselineValue = descriptor.extract(
            baselineScenario,
            baselineResult,
          );
          if (baselineValue != null) {
            deltaVsBaseline = value - baselineValue;
          }
        }
      }

      return {
        scenarioId: id,
        value,
        display: formatDisplay(descriptor.unit, value, fx),
        deltaVsBaseline,
        deltaDisplay: formatDelta(descriptor.unit, deltaVsBaseline, fx),
        caveated,
        ...(deltaSuppressed ? { deltaSuppressed } : {}),
        ...(basis !== undefined ? { basis } : {}),
      };
    });

    return {
      key: descriptor.key,
      label: descriptor.label,
      category: descriptor.category,
      unit: descriptor.unit,
      cells,
      ...(basisMixed ? { basisMixed: true } : {}),
    };
  });

  // Dated rows are calculated from the immutable dated input/ledger. They are
  // deliberately blank for legacy columns: numbers from different engines are
  // not a comparison basis.
  if (scenarios.some((scenario) => scenario.calculationMethod === "dated-v2"))
    for (const metric of datedMetrics(
      scenarios,
      valuationDate,
      endDate,
      datedByColumn,
    )) {
      const baselineDated = baselineScenario?.calculationMethod === "dated-v2";
      const cells: CompareCell[] = scenarioIds.map((id, index) => {
        const scenario = scenarios[index];
        const dated = datedByColumn[index];
        const value =
          scenario?.calculationMethod === "dated-v2"
            ? metric.value(scenario, dated)
            : null;
        const isBaseline = id === effectiveBaseline;
        const comparable = !isBaseline && baselineDated && value !== null;
        const baselineValue =
          baselineDated && baselineScenario
            ? metric.value(baselineScenario, datedByColumn[baselineIndex])
            : null;
        const ownershipMetric = metric.key.startsWith("ownership.");
        const fullSeriesReturn =
          metric.key.endsWith("Irr") ||
          metric.key === "datedReturns.equityMultiple";
        const returnField =
          fullSeriesReturn && metric.key.includes("project")
            ? "project"
            : fullSeriesReturn && metric.key.includes("equity")
              ? "equity"
              : null;
        const excludesReturnCashflows =
          returnField !== null &&
          excludesInvestmentCashflows(
            dated,
            returnField,
            valuationDate,
            endDate,
          );
        const unsettledNpv =
          metric.key.endsWith("Npv") &&
          dated.finance?.complete === true &&
          dated.finance.returnsSettled === false;
        const matchingOwnershipHorizon =
          !ownershipMetric ||
          (scenario?.datedOwnership?.valuationDate ===
            baselineScenario?.datedOwnership?.valuationDate &&
            scenario?.datedOwnership?.endDate ===
              baselineScenario?.datedOwnership?.endDate);
        const deltaVsBaseline =
          comparable &&
          baselineValue !== null &&
          metric.unit !== "date" &&
          matchingOwnershipHorizon
            ? value - baselineValue
            : null;
        return {
          scenarioId: id,
          value,
          display: formatDisplay(metric.unit, value, fx),
          deltaVsBaseline,
          deltaDisplay: formatDelta(metric.unit, deltaVsBaseline, fx),
          ...(!isBaseline && scenario?.calculationMethod !== "dated-v2"
            ? { deltaSuppressed: "method" as const }
            : {}),
          ...(!isBaseline &&
          scenario?.calculationMethod === "dated-v2" &&
          !baselineDated
            ? { deltaSuppressed: "method" as const }
            : {}),
          ...(!isBaseline && ownershipMetric && !matchingOwnershipHorizon
            ? {
                deltaSuppressed: "horizon" as const,
                basis: "source ownership horizon",
              }
            : ownershipMetric
              ? { basis: "source ownership horizon" }
              : {}),
          ...(excludesReturnCashflows
            ? {
                basis: "common window excludes investment cash flows",
                ...(!isBaseline ? { deltaSuppressed: "window" as const } : {}),
              }
            : {}),
          ...(unsettledNpv ? { basis: "remaining-debt NPV diagnostic" } : {}),
        };
      });
      rows.push({
        key: metric.key,
        label: metric.label,
        category: metric.category,
        unit: metric.unit,
        cells,
        ...(cells.some(
          (cell) =>
            cell.basis === "common window excludes investment cash flows" ||
            cell.basis === "remaining-debt NPV diagnostic",
        )
          ? { basisMixed: true }
          : {}),
      });
    }

  // ---- attach "what drives the gap" reads ----
  const rowByKey = (key: string) => rows.find((r) => r.key === key);

  const totalCostRow = rowByKey("costStack.totalCost");
  if (totalCostRow) {
    totalCostRow.driver = deriveTotalCostDriver(
      totalCostRow,
      rows,
      effectiveBaseline,
      scenarioIds,
      hasBaseline,
    );
  }

  const weightedYearRow = rowByKey("buildTiming.weightedOnlineYear");
  if (weightedYearRow) {
    weightedYearRow.driver = deriveWeightedYearDriver(
      weightedYearRow,
      rows,
      effectiveBaseline,
      scenarioIds,
      hasBaseline,
    );
  }

  return {
    scenarioIds,
    baselineId: effectiveBaseline,
    rows,
    marketIsStatic,
    scenarioNames: scenarios.map((s) => s.name),
    metadata: {
      valuationDate,
      endDate,
      commonHorizon: endDate,
      fx,
      changedInputs: changedInputInventory(
        scenarios,
        effectiveBaseline,
        scenarioIds,
      ),
      costBridge: resolvedCostBridge(
        datedByColumn,
        effectiveBaseline,
        scenarioIds,
      ),
    },
  } as CompareViewModelWithNames;
};

function stableValue(value: unknown): string {
  if (value === null || typeof value !== "object")
    return JSON.stringify(value) ?? "undefined";
  if (Array.isArray(value)) return `[${value.map(stableValue).join(",")}]`;
  return `{${Object.entries(value as Record<string, unknown>)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, item]) => `${key}:${stableValue(item)}`)
    .join(",")}}`;
}

function changedInputInventory(
  scenarios: Scenario[],
  baselineId: string,
  ids: string[],
): Record<string, string[]> {
  const baseline = scenarios[ids.indexOf(baselineId)];
  if (!baseline) return {};
  const result: Record<string, string[]> = {};
  for (let index = 0; index < scenarios.length; index += 1) {
    if (ids[index] === baselineId) continue;
    const candidate = scenarios[index];
    const changed: string[] = [];
    if (candidate.calculationMethod !== baseline.calculationMethod)
      changed.push("calculation basis");
    const add = (path: string, before: unknown, after: unknown) =>
      changed.push(
        `${path}: ${String(before ?? "—")} → ${String(after ?? "—")}`,
      );
    const compareCollection = (
      label: string,
      before: ReadonlyArray<{ id?: string }> | undefined,
      after: ReadonlyArray<{ id?: string }> | undefined,
      keys: string[],
    ) => {
      const keyed = (items: ReadonlyArray<{ id?: string }> | undefined) =>
        new Map((items ?? []).map((item, i) => [item.id ?? String(i), item]));
      const left = keyed(before);
      const right = keyed(after);
      for (const id of new Set([...left.keys(), ...right.keys()])) {
        const a = left.get(id) as Record<string, unknown> | undefined;
        const b = right.get(id) as Record<string, unknown> | undefined;
        for (const key of keys)
          if (stableValue(a?.[key]) !== stableValue(b?.[key]))
            add(`${label}[${id}].${key}`, a?.[key], b?.[key]);
      }
    };
    for (const key of ["startDate", "endDate", "discountRate", "equityBudget"])
      if (
        stableValue(
          baseline.datedFinance?.[key as keyof typeof baseline.datedFinance],
        ) !==
        stableValue(
          candidate.datedFinance?.[key as keyof typeof candidate.datedFinance],
        )
      )
        add(
          `datedFinance.${key}`,
          baseline.datedFinance?.[key as keyof typeof baseline.datedFinance],
          candidate.datedFinance?.[key as keyof typeof candidate.datedFinance],
        );
    compareCollection(
      "costs",
      baseline.datedFinance?.costs,
      candidate.datedFinance?.costs,
      ["category", "amount", "date", "milestone"],
    );
    compareCollection(
      "facilities",
      baseline.datedFinance?.facilities,
      candidate.datedFinance?.facilities,
      ["limit", "annualRate", "fundingShare", "equityMode", "maturityDate"],
    );
    compareCollection(
      "milestones",
      baseline.datedFinance?.milestones,
      candidate.datedFinance?.milestones,
      ["date"],
    );
    for (const key of ["valuationDate", "endDate", "annualDiscountRate"])
      if (
        stableValue(
          baseline.datedOwnership?.[
            key as keyof typeof baseline.datedOwnership
          ],
        ) !==
        stableValue(
          candidate.datedOwnership?.[
            key as keyof typeof candidate.datedOwnership
          ],
        )
      )
        add(
          `datedOwnership.${key}`,
          baseline.datedOwnership?.[
            key as keyof typeof baseline.datedOwnership
          ],
          candidate.datedOwnership?.[
            key as keyof typeof candidate.datedOwnership
          ],
        );
    if (
      stableValue(candidate.assumptions) !== stableValue(baseline.assumptions)
    )
      changed.push("legacy assumptions changed");
    result[ids[index]] = changed.slice(0, 12);
  }
  return result;
}

function resolvedCostBridge(
  datedByColumn: ReturnType<typeof datedWindowResult>[],
  baselineId: string,
  ids: string[],
): Record<string, number | null> {
  const base = datedByColumn[ids.indexOf(baselineId)]?.finance?.costBuckets;
  if (!base) return {};
  const bridge: Record<string, number | null> = {};
  for (let index = 0; index < datedByColumn.length; index += 1) {
    const next = datedByColumn[index].finance?.costBuckets;
    if (ids[index] === baselineId || !next) continue;
    for (const category of new Set([
      ...Object.keys(base),
      ...Object.keys(next),
    ]))
      bridge[`${ids[index]} · ${category} (source horizon)`] =
        (next[category] ?? 0) - (base[category] ?? 0);
  }
  return bridge;
}

function deriveTotalCostDriver(
  totalCostRow: CompareRow,
  allRows: CompareRow[],
  baselineId: string,
  scenarioIds: string[],
  hasBaseline: boolean,
): string | undefined {
  if (!hasBaseline) return undefined;
  const baselineIndex = scenarioIds.indexOf(baselineId);
  const baselineValue = totalCostRow.cells[baselineIndex]?.value;
  if (baselineValue == null) return undefined;

  const costKeys = [
    "costStack.hardConstruction",
    "costStack.preliminaries",
    "costStack.builderMargin",
    "costStack.consultantCosts",
    "costStack.statutoryCosts",
    "costStack.constructionContingency",
    "costStack.holdingInterest",
    "costStack.developmentManagementFee",
  ];

  let best: { key: string; label: string; delta: number } | null = null;
  for (const key of costKeys) {
    const sub = allRows.find((r) => r.key === key);
    if (!sub) continue;
    for (let i = 0; i < scenarioIds.length; i++) {
      if (i === baselineIndex) continue;
      const cell = sub.cells[i];
      if (cell.value == null) continue;
      const baseCell = sub.cells[baselineIndex];
      if (baseCell.value == null) continue;
      const delta = cell.value - baseCell.value;
      if (!best || Math.abs(delta) > Math.abs(best.delta)) {
        best = { key, label: sub.label, delta };
      }
    }
  }
  if (!best || best.delta === 0) return undefined;
  const direction = best.delta > 0 ? "+" : "−";
  const amount = Fmt.compactCurrency(Math.abs(best.delta));
  return `${direction}${amount} ${best.label.toLowerCase()}`;
}

function deriveWeightedYearDriver(
  weightedYearRow: CompareRow,
  allRows: CompareRow[],
  baselineId: string,
  scenarioIds: string[],
  hasBaseline: boolean,
): string | undefined {
  if (!hasBaseline) return undefined;
  const baselineIndex = scenarioIds.indexOf(baselineId);
  const nlaRow = allRows.find((r) => r.key === "buildTiming.totalNlaSqm");
  if (!nlaRow) return undefined;
  for (let i = 0; i < scenarioIds.length; i++) {
    if (i === baselineIndex) continue;
    const deltaYear =
      (weightedYearRow.cells[i].value ?? 0) -
      (weightedYearRow.cells[baselineIndex].value ?? 0);
    const deltaNla =
      (nlaRow.cells[i].value ?? 0) - (nlaRow.cells[baselineIndex].value ?? 0);
    if (deltaYear !== 0 || deltaNla !== 0) {
      const direction = deltaYear > 0 ? "slower" : "faster";
      const nlaText =
        deltaNla !== 0 ? `, ${Fmt.num(Math.abs(deltaNla), 0)} sqm NLA` : "";
      return `${direction} online${nlaText}`;
    }
  }
  return undefined;
}

function csvCell(v: unknown): string {
  const s =
    typeof v === "string" && /^[=+\-@\t]/.test(v) ? `'${v}` : String(v ?? "");
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function exportCompareCsv(vm: CompareViewModel): string {
  const vmNames = vm as CompareViewModelWithNames;
  const names = vmNames.scenarioNames ?? vm.scenarioIds;
  const { scenarioIds, baselineId, rows } = vm;

  const header = ["Metric"];
  for (let i = 0; i < scenarioIds.length; i++) {
    header.push(names[i] ?? scenarioIds[i]);
    header.push(`Δ vs ${names[scenarioIds.indexOf(baselineId)] ?? baselineId}`);
  }

  const lines: string[] = [header.map(csvCell).join(",")];
  if (vm.metadata) {
    const fx = vm.metadata.fx;
    lines.push(
      [
        "Report metadata",
        `valuationDate=${vm.metadata.valuationDate ?? ""}`,
        `endDate=${vm.metadata.endDate ?? ""}`,
        `commonHorizon=${vm.metadata.commonHorizon ?? ""}`,
        `fxSource=${fx?.source ?? ""}`,
        `fxAsOf=${fx?.asOf ?? ""}`,
        `fxRate=${fx?.rate ?? ""}`,
      ]
        .map(csvCell)
        .join(","),
    );
  }

  const categoryLabels: Record<CompareCategory, string> = {
    land: "Land",
    costStack: "Cost stack",
    buildTiming: "Build & timing",
    rent: "Rent",
    developerReturns: "Developer returns",
    operatorAffordability: "Operator affordability",
    market: "Market",
    programme: "Programme",
    funding: "Funding",
    ownership: "Ownership",
  };

  let currentCategory: CompareCategory | null = null;
  for (const row of rows) {
    if (row.category !== currentCategory) {
      currentCategory = row.category;
      const catCells = [
        categoryLabels[currentCategory],
        ...Array(scenarioIds.length * 2).fill(""),
      ];
      lines.push(catCells.map(csvCell).join(","));
    }
    const cells = [row.label];
    for (let i = 0; i < scenarioIds.length; i++) {
      const cell = row.cells[i];
      // The exported file has no pill or tooltip: mark a caveated fallback on the value itself, and
      // distinguish a suppressed delta from the baseline column's empty delta.
      // Wire format: "<display>[ (≈ est.)][ [<basis>]]" — the value first, then a caveat marker in
      // round brackets, then the exit basis in square brackets (only on mixed rows, on EVERY column
      // including the baseline so both bases are visible side by side).
      let display = cell.caveated ? `${cell.display} (≈ est.)` : cell.display;
      if (row.basisMixed && cell.basis) display = `${display} [${cell.basis}]`;
      cells.push(display);
      cells.push(
        cell.deltaDisplay ??
          (cell.deltaSuppressed === "self"
            ? "n/a (estimate)"
            : cell.deltaSuppressed === "baseline"
              ? "n/a (baseline is an estimate)"
              : cell.deltaSuppressed === "basis"
                ? "n/a (different exit basis)"
                : cell.deltaSuppressed === "window"
                  ? "n/a (window excludes investment cash flows)"
                  : ""),
      );
    }
    lines.push(cells.map(csvCell).join(","));
  }

  return lines.join("\n") + "\n";
}
